import { inject, Injectable } from '@angular/core';
import { AlertType } from '@gains-com/ui-angular';
import { ApiService, ErrorMapType } from './api.service';
import { decimal, PurchaseStatus } from '@gains-com/shared';
import { lastValueFrom } from 'rxjs';
import {
  Coinflow3DSOptions,
  CoinflowCardPaymentForm,
} from '../components/payments/common';
import { PurchaseAndPayment } from './payments-common';
import { environment } from '../../environments/environment';
import { AnalyticsTrackingService } from './analytics-tracking.service';
import {
  CoinflowChargeParams,
  CoinflowCheckoutResponse,
  CoinflowCheckoutService,
  coinflowCustomerInfo,
} from './coinflow-checkout.service';
import { UserViewService } from './view-states/user-view.service';

@Injectable({
  providedIn: 'root',
})
export class PurchaseService extends ApiService {
  readonly ErrorMap: ErrorMapType = {
    INTERNAL_SERVER_ERROR: {
      swallowError: false,
      title: 'Unknown Error',
      message:
        'An unknown problem occurred. Please contact customer support if the problem persists.',
      type: AlertType.Danger,
    },
    INVALID_PAYMENT_METHOD: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Redemption payment method is invalid. Try using a different payment method.',
    },
    INSUFFICIENT_FUNDS: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message: 'Insufficient SC to complete redemption.',
    },
    UNSUPPORTED_ASSET: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Asset is not supported for this operation. Try redeeming another asset.',
    },
    DUPLICATE_TRANSACTION: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message: 'Redemption has already been submitted.',
    },
    ...Object.fromEntries(
      [
        'INVALID_TRANSACTION',
        'COULD_NOT_RECONCILE_PAYMENT_USER',
        'COULD_NOT_RECONCILE_TRANSACTION',
        'TRANSACTION_FAILED',
        'AUTOFUEL_FAILED',
      ].map((error) => [
        error,
        {
          type: AlertType.Danger,
          swallowError: false,
          title: 'Redemption Failed',
          message: 'An unexpected error occurred. Please try again.',
        },
      ])
    ),
  };

  private readonly _analyticsTracking = inject(AnalyticsTrackingService);
  private readonly _coinflowCheckout = inject(CoinflowCheckoutService);
  private readonly _userView = inject(UserViewService);

  async getPurchase(id: string) {
    return await this.get<PurchaseAndPayment>(`/purchases/${id}`);
  }

  async coinflowCheckoutSavedCard(
    amount: number | string,
    coinflowSessionId: string,
    cardToken: string,
    authentication3DS: Coinflow3DSOptions
  ): Promise<CoinflowCheckoutResponse> {
    return this._coinflowCheckout.checkoutSavedCard(
      this._gamingChargeParams(amount, coinflowSessionId),
      cardToken,
      authentication3DS
    );
  }

  async coinflowCheckoutNewCard(
    amount: number | string,
    coinflowSessionId: string,
    card: CoinflowCardPaymentForm,
    authentication3DS: Coinflow3DSOptions
  ): Promise<CoinflowCheckoutResponse> {
    return this._coinflowCheckout.checkoutNewCard(
      this._gamingChargeParams(amount, coinflowSessionId),
      card,
      authentication3DS
    );
  }

  // The GAMING charge composition: static environment merchant, client-computed
  // cents, client-authored chargeback block. Merch composes its own params from
  // the server's charge block — the two must never share composition.
  private _gamingChargeParams(
    amount: number | string,
    coinflowSessionId: string
  ): CoinflowChargeParams {
    return {
      merchantId: environment.coinflow.merchantId,
      sessionKey: coinflowSessionId,
      subtotalCents: decimal(amount).mul(100).toNumber(),
      webhookInfo: this._analyticsTracking.getParams() ?? {},
      chargebackProtectionData: [
        {
          productName: 'Sweeps Coins',
          productType: 'gameOfSkill',
          quantity: amount,
          rawProductData: {
            currency: 'sweeps_coins',
            amount: amount,
          },
        },
      ],
      customerInfo: coinflowCustomerInfo(this._userView.state),
    };
  }

  getPurchases$(q?: {
    start?: Date;
    end?: Date;
    limit?: number;
    status?: PurchaseStatus | PurchaseStatus[];
    sortDirection?: 'asc' | 'desc';
  }) {
    const { start, end, limit, status, sortDirection } = q ?? {};

    const qs = new URLSearchParams({
      ...(start ? { start: start.toISOString() } : {}),
      ...(end ? { end: end.toISOString() } : {}),
      ...(limit ? { limit: limit.toString() } : {}),
      ...(sortDirection ? { sortDirection } : {}),
    });

    if (status) {
      Array.isArray(status)
        ? status.forEach((status) => qs.append('status', status))
        : qs.append('status', status);
    }

    return this.get$<{ total_count: number; results: PurchaseAndPayment[] }>(
      `/purchases?${qs.toString()}`
    );
  }

  async getPurchases(q?: {
    start?: Date;
    end?: Date;
    limit?: number;
    status?: PurchaseStatus | PurchaseStatus[];
  }) {
    return lastValueFrom(this.getPurchases$(q));
  }

  /**
   * Gets the most recent completed purchase for the authenticated user.
   * Returns payment method info used for document verification.
   */
  async getMostRecentCompletedPurchase(): Promise<MostRecentCompletedPurchase | null> {
    return await this.get<MostRecentCompletedPurchase | null>('/purchases/most-recent-completed');
  }
}

export interface MostRecentCompletedPurchase {
  purchase_id: string;
  payment_id: string;
  payment_method_entity_type_id: string;
  payment_method_entity_id: string;
  amount_usd: string;
  completed_at: string;
}
